import * as THREE from "three";
import { groundAt } from "../terrain";
import { Cell, Grid } from "@highmarch/domain";
import type { Biome } from "../../ui/chapters";
import { cellToWorld } from "../scene";
import { AnimRig } from "../rig";
import { HQ_ROCKS, HQ_YAW, cellHash, lib } from "./common";
import { BakedRange, bakeStatic } from "./staticBatch";
import { Contact, GroundContacts, detailSurvivesSlope, slopeAmount } from "../groundSlope";
import { windBand } from "../groundMesh";
import { HqMaterials } from "../hqMaterials";
import { Leafy, crownOf, dropLeaves } from "./foliageLeaves";
import { PropClearance } from "./propClearance";
import { BASE_CLEAR_HEIGHT, PORTAL_CLEAR, PROP_HEIGHT, PROP_POOLS, ROAD_CLEAR, ROAD_CLEAR_CELLS, baseRing, farLod, isLow, lowShipped, portalZone, propContact, scaleOf } from "./foliageKit";

/**
 * Blockers on blocked cells and small dressing on free cells (art/hq/README.md "Ground kit v4").
 *
 * Almost all of it is baked into a few merged static meshes (`bakeMap`): rocks, logs and stumps have no clips, and a
 * tree's or bush's clips were only its idle sway and an occasional gust — which the wind shader already does per
 * vertex (hqMaterials.ts). Placed as rigs, every tree ran an AnimationMixer and, as a skinned mesh, re-uploaded its
 * bone texture every frame; the hub alone paid fifteen texture uploads a frame for its bushes. Only props whose clips
 * do something the shader cannot (a campfire's flames, a lamp's flicker) stay live.
 */
export class FoliageView {
  readonly group = new THREE.Group();
  private readonly rigs: AnimRig[] = [];
  private readonly dressing = new Map<number, THREE.Object3D>();
  /** Props waiting for `bakeMap`, and where each baked prop's vertices landed so the road can hide dressing in place. */
  private readonly still = new THREE.Group();
  private readonly baked = new Map<THREE.Object3D, BakedRange[]>();
  /**
   * Families whose clips are only wind — the idle sway and the gust — and so bake static, the shader swaying them.
   * (The same families rigidRig.ts merges; any other prop with clips stays live.)
   */
  static readonly WindOnly = /^p_(tree_|bush|cypress|dead_willow|ash_tree)/;
  /** Side of a baked chunk on the map, in cells: the map is 9–15 cells wide, so this is a handful of draws. */
  static readonly MapChunk = 8;
  /** The wind burst a gust sends out (see `gustAround`): how strong at its centre. */
  static readonly GustStrength = 1;
  /**
   * The map's trees (their feet), baked static like the rest: the wind shader sways them and `gustAround` bends them.
   * Kept for the leaf fall, and each one's wind-band value last frame, so a flurry leaves as a band's crest ARRIVES.
   */
  private readonly trees: THREE.Vector3[] = [];
  private bandWas = new Float32Array(0);

  constructor() {
    // a new map: the previous one's contact shadows go with it (the ground re-stamps when the registry moves)
    GroundContacts.clear();
  }

  /**
   * The cells within `BaseClear` of the fortress's footprint, on every side. The camera snaps round the map in four
   * yaws, so "the two cells in front of the fortress" is a different pair of rows for each one; clearing the ring
   * covers all of them. Trees and tall blockers there hid the base from the default camera (towers F6d, terrain F5).
   */
  setBase(grid: Grid): void {
    this.baseZone.clear();
    for (const k of baseRing(grid, FoliageView.BaseClear)) this.baseZone.add(k);
  }
  private readonly baseZone = new Set<string>();
  /** Cells round the fortress kept clear of anything taller than `BaseClearHeight`. */
  static readonly BaseClear = 2;
  /** Tallest a prop may stand beside the fortress, in world units: a waist-high rock (the keep is ~1.4). */
  static readonly BaseClearHeight = BASE_CLEAR_HEIGHT;
  static readonly isLow = isLow;
  /** The portal clearance: tall props near the portal and the first road cells are hidden (propClearance.ts). */
  static readonly PortalClear = PORTAL_CLEAR;
  static readonly RoadClear = ROAD_CLEAR;
  static readonly RoadClearCells = ROAD_CLEAR_CELLS;
  /** Which props are hidden: tall ones in the portal clearance, dressing under the road (propClearance.ts). */
  private readonly clearance = new PropClearance(this.baked, this.group, () => FoliageView.pool().rocks);

  static readonly portalZone = portalZone;

  /** Re-applies the portal clearance for the road `road` (walking order, the portal first; empty: no road yet). */
  clearPortal(road: readonly { x: number; y: number }[]): void {
    this.clearance.zone = FoliageView.portalZone(road);
    this.clearance.apply();
  }

  /** Share of free grass cells that get a small prop, and the offset range inside the cell. */
  static readonly DressingShare = 0.07;
  static readonly DressingOffset = 0.25;
  /** The whole-cell blockers, trees, landmarks and dressing per biome (foliageKit.ts). */
  static readonly Pools = PROP_POOLS;
  /**
   * The biome the level is set in. The ground swaps its tiling maps per biome; the props have to swap with them or
   * the ashlands grow meadow oaks out of the cinders.
   */
  static biome: Biome = "meadow";
  /**
   * Hero props the level author placed by hand, keyed `x|y`. A blocker on one of these cells uses the named model
   * instead of drawing from the biome pool, which is how a level gets its landmark — the arch you route the road
   * under, the pair of basalt columns flanking the gate — rather than whatever the cell hash happened to pick.
   */
  static authored = new Map<string, string>();
  /** Replaces the authored set for a level (empty for a generated one). */
  static setAuthored(props: readonly { name: string; x: number; y: number }[]): void {
    FoliageView.authored = new Map(props.map((p) => [`${p.x}|${p.y}`, p.name]));
  }
  private static pool(): { rocks: string[]; trees: string[]; landmarks: string[]; dressing: string[] } {
    return FoliageView.Pools[FoliageView.biome] ?? FoliageView.Pools.meadow;
  }

  /**
   * Scatters the land beyond the playable rectangle.
   *
   * Props inside the map have a job: they block cells, which is what stops the drafted road running straight. Out
   * here they have none, so they stay sparse and thin out as the haze takes over — enough to say the world carries
   * on past the map, never a decorative ring. (An earlier pass crowded this with mesas and arches to make an island
   * read; the world is not an island.)
   */
  dressBeyond(land: Set<string>, grid: Grid, fogFull: number): void {
    // Everything placed out here is static scenery: built into a scratch group without clips, then baked into a few
    // merged meshes (staticBatch.ts). Placed one by one it was ~2,200 draw calls, 90% of a level's, all casting shadows.
    const scratch = new THREE.Group();
    this.far = scratch;
    const reach = Math.min(fogFull, FoliageView.BeyondReach);
    for (const k of land) {
      const [x, z] = k.split("|").map(Number);
      const c = new Cell(x, z);
      if (grid.inBounds(c)) continue;
      // distance in cells past the map edge, so the scatter can thin with it
      const out = Math.max(0, -x, x - (grid.width - 1), -z, z - (grid.height - 1));
      if (out > reach) continue;                                // past here the haze has all but taken it
      const thin = 1 - Math.min(1, out / fogFull);
      const h = cellHash(c);
      const roll = (h >> 5) % 100;
      this.farOut = out;
      if (roll < 8 * thin) this.addBlocker(c, 100000 + (h % 9973));
      else if (roll < 20 * thin) this.addDressing(c, 200000 + (h % 9973), true);
    }
    this.far = null;
    this.bakedMeshes.push(...bakeStatic(scratch, this.group, FoliageView.BakeChunk, { shadows: false, lod: (prop) => FoliageView.farLod(prop.userData.out as number), ranges: this.baked }));
    this.clearance.apply();                                  // a portal on the map edge clears the scenery beside it too
  }

  /**
   * Bakes the props placed on the map so far into static chunks that cast shadows. GridView calls it once it has
   * placed every blocker and dressing prop.
   */
  bakeMap(): void {
    if (this.still.children.length === 0) return;
    this.group.add(this.still);                        // bake into the group's space, which is where they stand
    this.bakedMeshes.push(...bakeStatic(this.still, this.group, FoliageView.MapChunk, { shadows: true, ranges: this.baked }));
    this.group.remove(this.still);
  }

  /** Frees the baked meshes' buffers: they are this view's own, unlike the live props, which share the library's. */
  dispose(): void {
    for (const m of this.bakedMeshes) { m.removeFromParent(); m.geometry.dispose(); }
    this.bakedMeshes.length = 0;
    this.baked.clear();
  }
  private readonly bakedMeshes: THREE.Mesh[] = [];

  static readonly farLod = farLod;
  /** While `dressBeyond` runs, props go here, unrigged, to be baked; `farOut` is how far out the current one stands. */
  private far: THREE.Group | null = null;
  private farOut = 0;
  /** Cells past the map edge that get scenery. The haze starts at 4 (GroundMesh.FogStart) and is thick by 7; beyond that the scenery cost triangles no one saw. */
  static readonly BeyondReach = 7;
  /** Side of a baked scenery chunk, in cells: big enough for few draws, small enough for culling to drop most. */
  static readonly BakeChunk = 12;

  /** A whole-cell blocker: rocks and trees flagged `blocks` in anchors.json, else the older prop pool. */
  addBlocker(c: Cell, index: number, from?: readonly string[]): void {
    const L = lib();
    if (!L) return;
    const h = cellHash(c);
    const named = FoliageView.authored.get(`${c.x}|${c.y}`);
    const nearBase = this.baseZone.has(`${c.x}|${c.y}`);
    // beside the fortress even an authored tree gives way: the base is the one thing that must always be seen
    if (named && L.hq(named) && (!nearBase || FoliageView.isLow(named))) {
      const prop = this.place(named, cellToWorld(c, groundAt(c.x, c.y)), HQ_YAW + ((h >> 4) % 4) * (Math.PI / 2));
      this.clearance.track(prop, c, named, true, this.far !== null, this.lastContact);
      if (named.startsWith("p_tree_")) this.addTree(prop, named);
      return;
    }
    const rocks = FoliageView.pool().rocks.filter((n) => L.hq(n));
    const trees = FoliageView.pool().trees.filter((n) => L.hq(n));
    let pool = from ? from.filter((n) => L.hq(n)) : (h >> 3) % 3 === 0 ? rocks : trees;
    if (pool.length === 0) pool = rocks.concat(trees);
    if (pool.length === 0) pool = HQ_ROCKS.filter((n) => L.hq(n));
    if (nearBase) {
      // still a blocker — the cell is blocked in the sim and has to look it — but a low one
      const low = lowShipped(rocks.concat(trees), L);
      if (low.length) pool = low;
    }
    if (pool.length === 0) return;
    const name = pool[h % pool.length];
    const prop = this.place(name, cellToWorld(c, groundAt(c.x, c.y)), HQ_YAW + ((h >> 4) % 4) * (Math.PI / 2));
    this.clearance.track(prop, c, name, true, this.far !== null, this.lastContact);
    if (name.startsWith("p_tree_")) this.addTree(prop, name);
    void index;
  }

  /** Small non-blocking prop on a free grass cell (a hashed share of them), offset inside the cell. */
  addDressing(c: Cell, index: number, always = false): void {
    const L = lib();
    if (!L) return;
    const h = cellHash(c);
    // an authored prop on a FREE cell is set dressing beside the road — a campfire, a cart, a signpost — and it is
    // placed whatever the dressing roll says
    const named = FoliageView.authored.get(`${c.x}|${c.y}`);
    if (!named && !always && (h >> 7) % 100 >= FoliageView.DressingShare * 100) return;
    const nearBase = this.baseZone.has(`${c.x}|${c.y}`);
    if (named && nearBase && !FoliageView.isLow(named)) return;
    const pool = FoliageView.pool().dressing.filter((n) => L.hq(n) && (!nearBase || FoliageView.isLow(n, 0.45)));
    if (!named && pool.length === 0) return;
    const name = named && L.hq(named) ? named : pool[(h >> 9) % pool.length];
    const o = FoliageView.DressingOffset;
    const at = cellToWorld(c, groundAt(c.x, c.y)).add(new THREE.Vector3((((h >> 11) % 100) / 100 - 0.5) * 2 * o, 0, (((h >> 13) % 100) / 100 - 0.5) * 2 * o));
    // scattered weeds and bushes keep off steep banks (M30 #16); an authored prop stands where it was put
    if (!named && !detailSurvivesSlope(slopeAmount(groundAt, at.x, at.z), ((h >> 17) % 1000) / 1000)) return;
    at.y = groundAt(at.x, at.z);                                   // on the ground where it stands, not at the cell's middle
    const prop = this.place(name, at, ((h >> 15) % 8) * (Math.PI / 4));
    this.dressing.set(index, prop);
    this.clearance.track(prop, c, name, false, this.far !== null, this.lastContact);
    if (named) this.authoredHere.add(index);
  }

  /**
   * Road cells lose their dressing (the road replaces the grass) — except a prop the level author placed by hand.
   *
   * Scattered dressing is noise and the road may cover it freely. A campfire or a signpost is somebody's decision,
   * and having the player erase it by drafting the road over that cell is a bug, not a feature.
   */
  setRoad(indices: readonly number[], road?: readonly { x: number; y: number }[]): void {
    if (road) this.clearance.zone = FoliageView.portalZone(road);
    const under = new Set(indices.filter((i) => !this.authoredHere.has(i)));
    this.clearance.underRoad = new Set([...this.dressing].filter(([i]) => under.has(i)).map(([, d]) => d));
    this.clearance.apply();
  }
  /** Dressing indices that came from the level's own prop list. */
  private readonly authoredHere = new Set<number>();

  /**
   * The live props' clips advance (the swaying ones are the wind shader's, and cost nothing here), and each broadleaf
   * tree sheds a flurry of leaves as a wind band's crest reaches it (terrain F7) — the same band that combs the grass
   * and sways every crown in the shader, so a gust visibly travels across the map.
   */
  update(dt: number): void {
    for (const r of this.rigs) r.update(dt);
    const n = this.trees.length;
    if (n === 0) return;
    if (this.bandWas.length !== n) this.bandWas = new Float32Array(n);
    const t = HqMaterials.time.value;
    for (let i = 0; i < n; i++) {
      const p = this.trees[i];
      const b = windBand(p.x, p.z, t);
      const gusted = b >= FoliageView.GustAt && this.bandWas[i] < FoliageView.GustAt;
      this.bandWas[i] = b;
      if (this.clearance.spotOf(this.treeProps[i])?.cleared) continue;
      dropLeaves(this.leafy[i], this.trees[i], dt, gusted);
    }
  }
  /** The trees' props (same order as `trees`): a tree the portal clearance hid sheds no leaves. */
  private readonly treeProps: THREE.Object3D[] = [];
  /** Band strength at which a tree sheds its gust flurry: well up the crest, so only the strong patches strip the oaks. */
  static readonly GustAt = 0.55;

  /** A gust through everything that sways within `radius` cells of a point (boss spawns): a burst in the wind shader. */
  gustAround(cell: { x: number; y: number }, radius = 2.5): void {
    HqMaterials.gust(cell.x, cell.y, radius, FoliageView.GustStrength);
  }

  /** The height each prop family is brought to, and the scale that brings it there (foliageKit.ts). */
  static readonly Height = PROP_HEIGHT;
  static readonly scaleOf = scaleOf;

  private place(name: string, at: THREE.Vector3, yaw: number): THREE.Object3D {
    const L = lib()!;
    const prop = L.clone(`hq:${name}`);
    prop.scale.multiplyScalar(FoliageView.scaleOf(name, prop));
    prop.position.copy(at);
    prop.rotation.y = yaw;
    this.lastContact = propContact(name, at);
    if (this.far) { prop.userData.out = this.farOut; this.far.add(prop); return prop; }   // baked scenery in the haze
    const clips = L.clipsOf(`hq:${name}`);
    if (clips.length === 0 || FoliageView.WindOnly.test(name)) { this.still.add(prop); return prop; }   // baked on the map
    this.group.add(prop);
    const rig = new AnimRig(prop, clips);
    rig.playLoop("idle", 1, true);
    this.rigs.push(rig);
    return prop;
  }

  /** The contact shadow the last `place` registered (for `track`). */
  private lastContact: Contact | null = null;

  private addTree(prop: THREE.Object3D, name: string): void {
    if (this.far) return;                              // scenery in the haze sheds nothing
    this.trees.push(prop.position.clone());
    this.treeProps.push(prop);
    // broadleaf trees shed leaves from their crown: measure where the crown is once, here
    this.leafy.push(crownOf(prop, name));
  }
  /** Per tree (same order as `trees`): its crown for leaf fall, or null for conifers and dead trees. */
  private readonly leafy: (Leafy | null)[] = [];
}
